import { createSelector } from 'reselect';

import { getCompletedFiatWithdrawals, getPendingFiatWithdrawals } from 'helpers/account';
import { ANTI_PHISHING_STATUS } from 'types/Account';
import IState from 'types/Istore';

// #region USER PROFILE SELECTORS
const selectUser = (state: IState) => state.user;

const selectMainAccount = createSelector(selectUser, user => {
  return user?.main_account ?? null;
});

const selectIsSubAccount = createSelector(selectUser, user => {
  return user?.is_sub_account ?? false;
});

const selectIsSubAccountCurrentlyLoggedIn = createSelector(
  selectIsSubAccount,
  selectMainAccount,
  (isSubAccount, mainAccount) => {
    return isSubAccount && mainAccount === null;
  }
);

const selectIsSubAccountHasSameEmail = createSelector(
  selectIsSubAccount,
  selectUser,
  selectMainAccount,
  (isSubAccount, user, mainAccount: any) => {
    return isSubAccount && user?.email === mainAccount?.email;
  }
);

const selectIsSubAccountHasDifferentEmail = createSelector(
  [selectIsSubAccount, selectUser],
  (isSubAccount, user) => isSubAccount && user?.is_password_set
);

const selectAntiPhishingCode = createSelector(
  selectUser,
  selectIsSubAccountHasSameEmail,
  (user, isSubAccountHasSameEmail) => {
    return isSubAccountHasSameEmail
      ? user?.main_account?.phishing_code ?? ''
      : user?.phishing_code ?? '';
  }
);

const selectUserEmail = createSelector(selectUser, user => user?.email ?? '');

const selectIsMfaEnabled = createSelector(
  selectUser,
  user => user?.isMfaEnabled ?? false
);
// #endregion

const selectAccount = (state: IState) => state.account;

// #region ANTI-PHISHING SELECTORS
const selectAntiPhishing = (state: IState) => state.account.security.antiPhishing;

const selectAntiPhishingStatus = createSelector(
  selectAntiPhishing,
  antiPhishing => antiPhishing.status
);

const selectAntiPhishingError = createSelector(
  selectAntiPhishing,
  antiPhishing => antiPhishing.errors
);

const selectIsAntiPhishingChangingMode = createSelector(
  [selectAntiPhishingStatus],
  status =>
    status === ANTI_PHISHING_STATUS.OPEN_CHANGE_CODE_MODAL ||
    status === ANTI_PHISHING_STATUS.SEND_VERIFICATION_CODE.LOAD ||
    status === ANTI_PHISHING_STATUS.SEND_VERIFICATION_CODE.FAIL
);

const selectIsSendingVerificationCodeForAntiPhishing = createSelector(
  [selectAntiPhishingStatus],
  status => status === ANTI_PHISHING_STATUS.SEND_VERIFICATION_CODE.LOAD
);

const selectIsVerifyingIdentityForAntiPhishingSent = createSelector(
  [selectAntiPhishingStatus],
  status =>
    status === ANTI_PHISHING_STATUS.SEND_VERIFICATION_CODE.SUCCESS ||
    status === ANTI_PHISHING_STATUS.UPDATE_CODE.LOAD ||
    status === ANTI_PHISHING_STATUS.UPDATE_CODE.FAIL
);

const selectIsVerifyingIdentityForAntiPhishingUpdate = createSelector(
  [selectAntiPhishingStatus],
  status => status === ANTI_PHISHING_STATUS.UPDATE_CODE.LOAD
);
// #endregion

// #region FiatWithdrawalHistory

const selectAccountFiatWithdrawalHistory = createSelector([selectAccount], account => {
  return account.fiatWithdrawalHistory;
});

const selectFiatWithdrawalHistoryApiStatus = createSelector(
  [selectAccountFiatWithdrawalHistory],
  fiatWithdrawalHistory => fiatWithdrawalHistory.apiStatus
);

const selectFiatWithdrawalHistoryData = createSelector(
  [selectAccountFiatWithdrawalHistory],
  fiatWithdrawalHistory => fiatWithdrawalHistory.tableData
);

const selectPendingAndCompletedFiatWithdrawalHistory = createSelector(
  [selectFiatWithdrawalHistoryData],
  data => ({
    pendingData: getPendingFiatWithdrawals(data),
    completedData: getCompletedFiatWithdrawals(data),
  })
);

// #endregion

const selectRefetchWithdrawalHistoryIndia = createSelector([selectAccount], account => {
  return account.refetchWithdrawalHistoryIndia;
});

const selectRefetchDepositHistoryIndia = createSelector([selectAccount], account => {
  return account.refetchDepositHistoryIndia;
});

const selectMfaActiveTab = createSelector([selectAccount], account => {
  return account?.security?.mfa?.activeTab;
});

const accountLeverageSelector = createSelector([selectAccount], account => {
  return account.leveragePreferences;
});

const selectUserNriStatusLog = createSelector([selectAccount], account => {
  return account.userNriStatusLog;
});

export {
  selectAccountFiatWithdrawalHistory,
  selectAntiPhishingCode,
  selectAntiPhishingError,
  selectFiatWithdrawalHistoryApiStatus,
  selectIsAntiPhishingChangingMode,
  selectIsMfaEnabled,
  selectIsSendingVerificationCodeForAntiPhishing,
  selectIsSubAccount,
  selectIsSubAccountCurrentlyLoggedIn,
  selectIsSubAccountHasDifferentEmail,
  selectIsSubAccountHasSameEmail,
  selectIsVerifyingIdentityForAntiPhishingSent,
  selectIsVerifyingIdentityForAntiPhishingUpdate,
  selectMainAccount,
  selectMfaActiveTab,
  selectPendingAndCompletedFiatWithdrawalHistory,
  selectRefetchDepositHistoryIndia,
  selectRefetchWithdrawalHistoryIndia,
  selectUserEmail,
  accountLeverageSelector,
  selectFiatWithdrawalHistoryData,
  selectUserNriStatusLog,
};
